import { assumedConsentMethod, csvHeaders } from "./defaults";
import type { CsvValidationResult, UploadRow } from "./types";

export function parseAndValidateCsv(content: string): CsvValidationResult {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return {
      summary: { totalRows: 0, validRows: 0, rejectedRows: 0 },
      rows: [],
    };
  }

  const [headerLine, ...dataLines] = lines;
  const headers = splitCsvLine(headerLine);
  const expectedHeaders = Array.from(csvHeaders);
  const rows: UploadRow[] = [];
  const assumedConsentReceivedAt = new Date().toISOString();

  if (
    headers.length !== expectedHeaders.length ||
    expectedHeaders.some((header, index) => headers[index] !== header)
  ) {
    return {
      summary: {
        totalRows: dataLines.length,
        validRows: 0,
        rejectedRows: dataLines.length,
      },
      rows: dataLines.map((_, index) => ({
        row_number: index + 2,
        phone_number: "",
        normalized_phone_number: null,
        consent_received_at: assumedConsentReceivedAt,
        consent_method: assumedConsentMethod,
        consent_attested_by: "",
        status: "rejected_invalid",
        rejection_reason: `Expected CSV headers: ${expectedHeaders.join(", ")}`,
      })),
    };
  }

  const seenPhoneNumbers = new Set<string>();

  for (const [index, line] of dataLines.entries()) {
    const rowNumber = index + 2;
    const values = splitCsvLine(line);
    const [
      phone_number = "",
      consent_attested_by = "",
    ] = values.map((value) => value.trim());

    let status: UploadRow["status"] = "valid";
    let rejectionReason: string | null = null;

    if (values.length !== expectedHeaders.length) {
      status = "rejected_invalid";
      rejectionReason = "Row does not contain exactly two columns.";
    } else if (!normalizePhoneNumber(phone_number)) {
      status = "rejected_invalid";
      rejectionReason = "Phone number must contain 10 or 11 digits.";
    } else if (!consent_attested_by) {
      status = "rejected_invalid";
      rejectionReason = "Consent attested by is required.";
    } else {
      const normalizedPhoneNumber = normalizePhoneNumber(phone_number)!;
      if (seenPhoneNumbers.has(normalizedPhoneNumber)) {
        status = "rejected_duplicate";
        rejectionReason = "Duplicate phone number in this upload.";
      } else {
        seenPhoneNumbers.add(normalizedPhoneNumber);
      }
    }

    rows.push({
      row_number: rowNumber,
      phone_number,
      normalized_phone_number: normalizePhoneNumber(phone_number),
      consent_received_at: assumedConsentReceivedAt,
      consent_method: assumedConsentMethod,
      consent_attested_by,
      status,
      rejection_reason: rejectionReason,
    });
  }

  const validRows = rows.filter((row) => row.status === "valid").length;

  return {
    summary: {
      totalRows: rows.length,
      validRows,
      rejectedRows: rows.length - validRows,
    },
    rows,
  };
}

function splitCsvLine(line: string) {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];

    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

export function normalizePhoneNumber(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  return null;
}
